import * as React from "react";
import * as ReactDOM from "react-dom";
import { registerLWPComponent } from "VSS/LWP";
import Performance = require("VSS/Performance");
import Resources = require("Dashboards/Scripts/Resources/TFS.Resources.Dashboards");
import { WidgetProps, LoadingStatus, WidgetPresentationMode } from "ModernWidgetContracts/WidgetProps";
import { ErrorMessageHelper } from "Dashboards/Scripts/Common";
import { WidgetFactory, WidgetFactoryOptions, IWidgetInternal } from "Dashboards/Scripts/WidgetHost";
import { IWidgetHost_ChildContracts, WidgetService_WidgetContext } from "Dashboards/Scripts/Contracts";
import TFS_Dashboards_Contracts = require("TFS/Dashboards/Contracts");
import TFS_Dashboards_WidgetContracts = require("TFS/Dashboards/WidgetContracts");
import { WidgetSettings } from "TFS/Dashboards/WidgetContracts";
import { WidgetSizeConverter } from "TFS/Dashboards/WidgetHelpers";
import { isFirstPartyContribution, loadContribution, interpretStatus, extractRelativeContributionId } from "./LegacyHostUtilities";
import { format } from "VSS/Utils/String";

// Note: Legacy has a hard, compile-time only dependency on Widget Host Project to expose contract definition for WidgetHosts.
// New plat has an run-time only dependency (through LWP) on Legacy Dashboard Stack for this Adapter and it's dependencies, with no build relationship

/** Exposes a New-Platform compatible entry point for Hosting of Legacy Widgets.
 *
 * Responsible for transforming widget data to legacy format, and transforming callbacks into modern shape.
 */
export class LegacyWidgetHostAdapter extends React.Component<WidgetProps> {
    public static readonly componentType = "LegacyWidgetHostAdapter";
    private contributedControl: IWidgetInternal;

    /** Legacy widgets need to be able to complete an initial load, before we attempt to reload.
     * Drag and drop scenario can trigger a fast reload with grid layout change, before widget has opportunity to finish load.
     */
    private widgetHasRespondedToLoad: boolean;

    public constructor(props: WidgetProps) {
        super(props);
    }

    public render(): JSX.Element {
        return <div className="classic-widget-container" style={this.getSize()} />;
    }

    private getSize() {
        return { width: this.props.size.width, height: this.props.size.height };
    }
    private stringEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    public shouldComponentUpdate?(nextProps: Readonly<WidgetProps>): boolean {
        // Behave semantically as a pure component - updates are only needed where Widget configuration props or state data has changed.
        // In particular, mode switching to config doesn't need an immediate re-render
        const needsReRender =
            this.props.name !== nextProps.name ||
            this.props.settings !== nextProps.settings ||
            !this.stringEqual(this.props.settingsVersion, nextProps.settingsVersion) ||
            !this.stringEqual(this.props.size, nextProps.size);
        return needsReRender;
    }

    public componentDidMount(): void {
        this.ensureWidgetIsInstantiated();
    }

    public componentWillUnmount(): void {
        this.ensureWidgetIsDisposed();
    }

    public componentDidUpdate(): void {
        // Ask the Widget to re-paint itself with the new context, if supports reload, and was properly handled the load() cycle(with failure or success).
        if (this.contributedControl != null && this.contributedControl.reload != null && this.widgetHasRespondedToLoad) {
            try {
                this.contributedControl.reload(convertHostPropsToWidgetSettings(this.props)).then(null, error => {
                    this.passErrorToHost(Resources.WidgetHostAdapter_ReloadError, "VS403674", error);
                });
            } catch (error) {
                this.passErrorToHost(Resources.WidgetHostAdapter_ReloadError, "VS403675", error);
            }
        } else {
            // Trash the previous instance if it doesn't honor reload or hasn't completed an initial load cycle.
            this.ensureWidgetIsInstantiated();
        }
    }

    private ensureWidgetIsInstantiated() {
        this.ensureWidgetIsDisposed();

        const container = ReactDOM.findDOMNode(this);
        const $wrappedContainer = $(container);

        if (this.props) {
            // Load the Third Party Widget Contribution through legacy Widget Host Implementation/Contribution Service
            loadContribution(this.props.contributionId).then(
                (legacyExtensionContribution: IExtensionContribution) => {
                    const isFirstParty = isFirstPartyContribution(legacyExtensionContribution);
                    const widgetData = convertHostPropsToLegacyWidgetData(this.props);
                    const hostContracts = convertHostPropsToLegacyHostContract($wrappedContainer, this.props);
                    WidgetFactory.renderWidgetContributionImpl(isFirstParty, widgetData, hostContracts, this.props.teamContext).then(
                        widgetContribution => {
                            if (widgetContribution == null) {
                                this.passErrorToHost(Resources.WidgetHostAdapter_ContributionLoadError, "VS403675", {});
                            } else {
                                this.contributedControl = widgetContribution;
                                const widgetSettings = convertHostPropsToWidgetSettings(this.props);

                                let preloadPromise;
                                try {

                                    if (this.props.presentationMode == WidgetPresentationMode.lightboxView && this.contributedControl.lightbox != null)
                                        preloadPromise = this.contributedControl.lightbox(widgetSettings, this.props.size);
                                    else
                                        // Determine Preload step for widgets which implement it.
                                        preloadPromise =
                                            this.contributedControl.preload != null
                                                ? this.contributedControl.preload(widgetSettings)
                                                : (Promise.resolve<TFS_Dashboards_WidgetContracts.WidgetStatus>(null) as IPromise<
                                                    TFS_Dashboards_WidgetContracts.WidgetStatus
                                                >);

                                    // operate on the promise
                                    preloadPromise.then(
                                        widgetStatus => {
                                            // Perform main Widget Load step.
                                            try {
                                                this.contributedControl.load(widgetSettings).then(
                                                    (widgetStatus: TFS_Dashboards_WidgetContracts.WidgetStatus) => {
                                                        this.props.onWidgetLoaded(interpretStatus(widgetStatus));
                                                        this.widgetHasRespondedToLoad = true;
                                                    },
                                                    error => {
                                                        this.passErrorToHost(Resources.WidgetHostAdapter_LoadError, "VS403677", error);
                                                        this.widgetHasRespondedToLoad = true;
                                                    }
                                                );
                                            } catch (error) {
                                                this.passErrorToHost(Resources.WidgetHostAdapter_LoadError, "VS403681", error);
                                            }
                                        },
                                        error => {
                                            this.passErrorToHost(Resources.WidgetHostAdapter_PreloadError, "VS403678", error);
                                        }
                                    );
                                } catch (error) {
                                    this.passErrorToHost(Resources.WidgetHostAdapter_PreloadError, "VS403676", error);
                                }
                            }
                        },
                        error => {
                            this.passErrorToHost(Resources.WidgetHostAdapter_ContributionLoadError, "VS403679", error);
                        }
                    );
                },
                error => {
                    this.passErrorToHost(Resources.WidgetHostAdapter_ContributionLoadError, "VS403680", error);
                }
            );
        }
    }

    private passErrorToHost(errorTextFormat: string, vsErrorId: string, errorObject: any) {
        const errorDetail = ErrorMessageHelper.parseErrorToString(errorObject);
        this.props.onWidgetLoaded({
            outcome: LoadingStatus.failure,
            outcomeDescription: format(errorTextFormat, vsErrorId, errorDetail)
        });
    }

    private ensureWidgetIsDisposed(): void {
        if (this.contributedControl) {
            if (this.contributedControl.dispose) {
                this.contributedControl.dispose();
            }
            this.contributedControl = null;
        }

        // Clear any dangling Content contained in the Legacy Host
        const container = ReactDOM.findDOMNode(this);
        $(container).empty();
    }
}
registerLWPComponent(LegacyWidgetHostAdapter.componentType, LegacyWidgetHostAdapter);

/**
 * Prepares callbacks required of a WidgetHost to render a contribution.
 */
function convertHostPropsToLegacyHostContract(widgetRenderingContainer: JQuery, props: WidgetProps): IWidgetHost_ChildContracts {
    const nop = (): any => { };
    const widgetHost: IWidgetHost_ChildContracts = {
        // Configuration callback
        configure: props.onConfigure,

        // Proprietary Legacy scheme for rendering into.
        getWidgetContainer: () => {
            return widgetRenderingContainer;
        },

        // Exposes info about widget for use by Widget Service.
        getWidget: () => {
            return convertHostPropsToWidgetServiceContext(props);
        },

        // This adapter is running in a context which does not currently have a perf scenario to hook into.
        // Provide enough for unported 1st party legacy consumers to not throw  null-refs, until the widgets can be modernized.
        getPerformanceScenario: () => {
            return {
                getPerformanceScenario: nop,
                getTelemetry: nop,
                setServiceInstanceType: nop,
                abort: nop,
                addAjaxCallStart: nop,
                addData: nop,
                addSplitTiming: nop,
                end: nop,
                getAjaxCalls: nop,
                getCorrelationId: nop,
                getData: nop,
                getDuration: nop,
                getFeatureArea: nop,
                getName: nop,
                getServiceInstanceType: nop,
                getSplitTimings: nop,
                getStartTime: nop,
                isActive: nop,
                isPageInteractive: nop,
                log: nop
            } as Performance.IScenarioDescriptor;
        }
    };
    return widgetHost;
}

/** Prepares data for use by the Widget Service. */
function convertHostPropsToWidgetServiceContext(props: WidgetProps): WidgetService_WidgetContext {
    return {
        id: props.id
    };
}

/** Prepares data to supply for rendering the contribution */
function convertHostPropsToLegacyWidgetData(props: WidgetProps): WidgetFactoryOptions {
    return {
        contributionId: props.contributionId,
        typeId: extractRelativeContributionId(props.contributionId) // `typeId` is the relative contribution ID.
    };
}

function convertHostPropsToWidgetSettings(props: WidgetProps): WidgetSettings {
    return {
        name: props.name,
        customSettings: convertWidgetHostPropsToCustomSettings(props),
        lightboxOptions: null,
        size: convertWidgetHostPropsToLegacySize(props)
    };
}

function convertWidgetHostPropsToCustomSettings(props: WidgetProps): TFS_Dashboards_WidgetContracts.CustomSettings {
    return {
        data: props.settings,
        version: props.settingsVersion
    };
}

/** Convert Modern pixel size to Column centric Sizes */
function convertWidgetHostPropsToLegacySize(props: WidgetProps): TFS_Dashboards_Contracts.WidgetSize {
    return {
        columnSpan: Math.ceil((props.size.width + 10) / (WidgetSizeConverter.GetWidgetWidth() + 10)),
        rowSpan: Math.ceil((props.size.height + 10) / (WidgetSizeConverter.GetWidgetHeight() + 10))
    };
}
